import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, Trash2, Eye, ChevronLeft, ChevronRight, Users, Plus, UserPlus, RefreshCw } from 'lucide-react';
import RecipientDetailModal, { SITUACAO } from './RecipientDetailModal';
import followupService from '../../services/global/followupService';

const PAGE_SIZE = 20;

const formatBRPhone = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
};

const rawDigits = (value) => value.replace(/\D/g, '');

// Extract phone from recipient (handles both old string and new object format)
const getPhone = (r) => (typeof r === 'string' ? r : r?.phone || '');
const getEnrolledAt = (r) => (typeof r === 'string' ? null : r?.enrolled_at || null);

const RecipientsTab = ({ followup, history, colors, styles, onRemoveRecipient, onRefresh, onAddRecipients, onOpenContactPicker, onResendDay }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [detailPhone, setDetailPhone] = useState(null);
  const [newNumber, setNewNumber] = useState('');

  const recipients = followup.recipients || [];

  // Situação calculada pelo backend (dia real, parado, pendências) — uma chamada por follow-up
  const [situacoes, setSituacoes] = useState({});
  const [loadingSit, setLoadingSit] = useState(false);
  const [filtroSit, setFiltroSit] = useState('todos');
  const loadSituacoes = useCallback(async () => {
    if (!followup?.id) return;
    setLoadingSit(true);
    try {
      const r = await followupService.listParticipantes(followup.id);
      const map = {};
      (r.data || []).forEach((p) => { map[p.phone] = p; });
      setSituacoes(map);
    } catch (e) {
      console.error('Error loading participant status:', e);
    } finally {
      setLoadingSit(false);
    }
  }, [followup?.id]);
  useEffect(() => { loadSituacoes(); }, [loadSituacoes, history]);

  // Compute stats per recipient from history
  const recipientStats = useMemo(() => {
    const stats = {};
    history.forEach(h => {
      if (!stats[h.recipient]) {
        stats[h.recipient] = { daysSet: new Set(), msgsSent: 0, totalMsgs: 0 };
      }
      stats[h.recipient].totalMsgs++;
      if (h.status === 'sent') {
        stats[h.recipient].daysSet.add(h.day);
        stats[h.recipient].msgsSent++;
      }
    });
    return stats;
  }, [history]);

  const filtered = recipients.filter(r =>
    (!searchTerm || getPhone(r).includes(searchTerm)) &&
    (filtroSit === 'todos' || (situacoes[getPhone(r)]?.status || '') === filtroSit)
  );
  const contagem = useMemo(() => {
    const c = {};
    recipients.forEach((r) => { const st = situacoes[getPhone(r)]?.status; if (st) c[st] = (c[st] || 0) + 1; });
    return c;
  }, [recipients, situacoes]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  const handleRemove = (phone) => {
    if (window.confirm(`Remover ${phone} dos destinatarios?`)) {
      onRemoveRecipient(phone);
    }
  };

  const handleAddManual = () => {
    const digits = rawDigits(newNumber);
    if (digits.length < 10 || digits.length > 11) {
      alert('Numero invalido. Informe DDD + numero (10 ou 11 digitos).');
      return;
    }
    const normalized = digits.length <= 11 ? `55${digits}` : digits;
    onAddRecipients([normalized]);
    setNewNumber('');
  };

  return (
    <>
      {/* Add recipients */}
      <div style={{
        ...styles.card, marginBottom: '16px', cursor: 'default',
        display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap',
      }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <span style={{
            position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
            fontSize: '14px', color: colors.textTertiary, fontWeight: 500, pointerEvents: 'none',
          }}>
            +55
          </span>
          <input
            value={newNumber}
            onChange={e => {
              const digits = rawDigits(e.target.value);
              if (digits.length <= 11) setNewNumber(formatBRPhone(digits));
            }}
            onKeyDown={e => { if (e.key === 'Enter') handleAddManual(); }}
            placeholder="(11) 99999-9999"
            style={{ ...styles.input, paddingLeft: '46px' }}
            maxLength={16}
          />
        </div>
        <button
          onClick={handleAddManual}
          disabled={rawDigits(newNumber).length < 10}
          style={{
            ...styles.btn, ...styles.btnPrimary,
            padding: '8px 14px', fontSize: '13px',
            opacity: rawDigits(newNumber).length >= 10 ? 1 : 0.5,
          }}
        >
          <Plus size={14} /> Adicionar
        </button>
        {onOpenContactPicker && (
          <button
            onClick={onOpenContactPicker}
            style={{ ...styles.btn, ...styles.btnOutline, padding: '8px 14px', fontSize: '13px' }}
          >
            <UserPlus size={14} /> Buscar contatos
          </button>
        )}
      </div>

      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ position: 'relative', maxWidth: '400px' }}>
          <Search size={16} style={{
            position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
            color: colors.textSecondary,
          }} />
          <input
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
            placeholder="Buscar numero..."
            style={{ ...styles.input, paddingLeft: '34px' }}
          />
        </div>
      </div>

      {/* Stats + filtro por situação */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap',
        fontSize: '13px', color: colors.textSecondary,
      }}>
        <Users size={14} />
        <span>{recipients.length} participante{recipients.length !== 1 ? 's' : ''}</span>
        {searchTerm && <span> ({filtered.length} encontrado{filtered.length !== 1 ? 's' : ''})</span>}
        <span style={{ flex: 1 }} />
        {['todos', 'em_dia', 'em_espera', 'pendente', 'falha', 'concluido'].map((k) => {
          const cfg = k === 'todos' ? { label: 'Todos', color: colors.textSecondary } : SITUACAO[k];
          const n = k === 'todos' ? recipients.length : (contagem[k] || 0);
          const active = filtroSit === k;
          return (
            <button key={k} onClick={() => { setFiltroSit(k); setPage(1); }} style={{
              padding: '4px 10px', borderRadius: 14, border: `1px solid ${active ? cfg.color : colors.borderColor}`,
              backgroundColor: active ? `${cfg.color}20` : 'transparent', color: active ? cfg.color : colors.textSecondary,
              fontSize: 12, cursor: 'pointer', fontWeight: active ? 700 : 500,
            }}>
              {cfg.label} {n ? `(${n})` : ''}
            </button>
          );
        })}
        <button onClick={loadSituacoes} title="Atualizar situação" style={{ border: 'none', background: 'transparent', color: colors.textSecondary, cursor: 'pointer' }}>
          <RefreshCw size={14} className={loadingSit ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '40px 20px', color: colors.textSecondary, fontSize: '14px',
        }}>
          {recipients.length === 0 ? 'Nenhum destinatario adicionado.' : 'Nenhum resultado encontrado.'}
        </div>
      ) : (
        <div style={{
          backgroundColor: colors.bgPrimary, borderRadius: '12px',
          border: `1px solid ${colors.borderColor}`, overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.borderColor}` }}>
                <th style={{ textAlign: 'left', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Numero
                </th>
                <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Inscrito em
                </th>
                <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Dia atual
                </th>
                <th style={{ textAlign: 'left', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Situacao
                </th>
                <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Dias recebidos
                </th>
                <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Msgs enviadas
                </th>
                <th style={{ textAlign: 'right', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Acoes
                </th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map(recipient => {
                const phone = getPhone(recipient);
                const enrolledAt = getEnrolledAt(recipient);
                const stat = recipientStats[phone];
                const daysReceived = stat ? stat.daysSet.size : 0;
                const msgsSent = stat ? stat.msgsSent : 0;
                const totalDays = followup.total_days || 0;
                const sit = situacoes[phone];
                const currentDay = sit?.dia_calendario ?? (enrolledAt
                  ? Math.floor((new Date().setHours(0,0,0,0) - new Date(enrolledAt + 'T00:00:00').getTime()) / 86400000) + 1
                  : null);
                const cappedDay = currentDay && totalDays ? Math.min(currentDay, totalDays) : currentDay;
                const isCompleted = sit ? sit.status === 'concluido' : (cappedDay && totalDays && cappedDay >= totalDays);
                const sitCfg = sit ? (SITUACAO[sit.status] || { label: sit.status, color: colors.textSecondary }) : null;
                return (
                  <tr key={phone} style={{ borderBottom: `1px solid ${colors.borderColor}20` }}>
                    <td style={{ padding: '10px 14px', color: colors.textPrimary, fontFamily: 'monospace', fontSize: '13px' }}>
                      {phone}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center', color: colors.textSecondary, fontSize: '12px' }}>
                      {enrolledAt ? new Date(enrolledAt + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      {cappedDay ? (
                        <span style={{
                          padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                          backgroundColor: isCompleted ? '#22c55e20' : '#3b82f620',
                          color: isCompleted ? '#22c55e' : '#3b82f6',
                        }}>
                          {isCompleted ? 'Concluido' : `${cappedDay}/${totalDays}`}
                        </span>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'left' }}>
                      {sitCfg ? (
                        <div>
                          <span style={{
                            padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                            backgroundColor: `${sitCfg.color}20`, color: sitCfg.color,
                          }}>
                            {sitCfg.label}
                          </span>
                          {sit.status === 'em_espera' && (
                            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 3 }}>
                              parado no dia {sit.dia_parado} há {sit.espera_dias} dia(s)
                            </div>
                          )}
                          {sit.status === 'pendente' && (
                            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 3 }}>
                              {sit.pendentes} msg(s) em reenvio automático
                            </div>
                          )}
                          {sit.status === 'falha' && (
                            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 3 }}>
                              dia(s) {sit.falhas_dias.join(', ')}: use Reenviar
                            </div>
                          )}
                        </div>
                      ) : <span style={{ color: colors.textSecondary, fontSize: 11 }}>{loadingSit ? '…' : '-'}</span>}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center', color: colors.textPrimary }}>
                      {daysReceived}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center', color: colors.textPrimary }}>
                      {msgsSent}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setDetailPhone(phone)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            background: 'none', border: `1px solid ${colors.borderColor}`,
                            borderRadius: '6px', padding: '4px 10px', fontSize: '11px',
                            color: colors.textSecondary, cursor: 'pointer',
                          }}
                          title="Ver detalhes"
                        >
                          <Eye size={12} /> Detalhes
                        </button>
                        <button
                          onClick={() => handleRemove(phone)}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'none', border: `1px solid #ef444430`,
                            borderRadius: '6px', padding: '4px 8px',
                            color: '#ef4444', cursor: 'pointer',
                          }}
                          title="Remover destinatario"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '12px', marginTop: '16px',
        }}>
          <button
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            style={{
              ...styles.btn, ...styles.btnOutline, padding: '6px 12px', fontSize: '12px',
              opacity: currentPage === 1 ? 0.4 : 1,
              cursor: currentPage === 1 ? 'default' : 'pointer',
            }}
          >
            <ChevronLeft size={14} /> Anterior
          </button>
          <span style={{ fontSize: '13px', color: colors.textSecondary }}>
            {currentPage} de {totalPages}
          </span>
          <button
            onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            style={{
              ...styles.btn, ...styles.btnOutline, padding: '6px 12px', fontSize: '12px',
              opacity: currentPage === totalPages ? 0.4 : 1,
              cursor: currentPage === totalPages ? 'default' : 'pointer',
            }}
          >
            Proximo <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Detail Modal */}
      <RecipientDetailModal
        isOpen={detailPhone !== null}
        phone={detailPhone}
        history={history}
        onClose={() => setDetailPhone(null)}
        colors={colors}
        followupId={followup.id}
        totalDays={followup.total_days}
        situacao={detailPhone ? situacoes[detailPhone] : null}
        onChanged={async () => { if (onRefresh) await onRefresh(); await loadSituacoes(); }}
        onRemove={(phone) => { onRemoveRecipient(phone); setDetailPhone(null); }}
        onResend={onResendDay ? (day) => onResendDay(day, detailPhone) : undefined}
      />
    </>
  );
};

export default RecipientsTab;
